// ContextPanel — the entity context surface (ADR-036 §5): ONE component, TWO
// hosts. Same shell (kind eyebrow · title · status · the top row of acts ·
// scroll body · footer), mounted two ways:
//
//   host="peek"  — the panel's FACE inside the peek's sheet (`PeekSheet`, below).
//     The SHEET is the overlay — the entrance, focus in/return, Escape, and
//     MODALITY FOLLOWS WIDTH (ADR-036 §2):
//       • ≥ md  → a NON-MODAL side panel docked at the inline-end edge; the list
//         BEHIND it stays interactive (no scrim, no focus trap). React Aria has
//         no docked-non-modal primitive, so this one panel is hand-rolled (a
//         role="dialog" region + Escape-close + polite focus-in/return) — the
//         ADR-028 hand-roll trip-wire.
//       • < md  → the modal SlideOver carrying the face whole (scrim, focus trap,
//         Escape, scroll-lock from React Aria).
//     THE HOST OWNS THE SHEET: PeekHost keeps ONE mounted for as long as
//     `?peek=` is set, and an entity's peek renders only this face inside it —
//     so the record arriving, a step to another row and a link to another kind
//     of record swap the face in place and never replay the entrance
//     (DESIGN-TOKENS §Motion: the sheet is never remounted). A peek no host
//     renders (the file sheet, the import run) wraps its face in its own sheet.
//     The open peek is URL view state (`useEntityPeek` → `?peek=`); REPLACE-
//     never-stack means an EntityRef inside a peek rewrites the same param — it
//     is FOLLOWED: the record left joins the way back, and the back chevron
//     takes the leading slot (named for it; X still closes the whole peek).
//
//   host="rail"  — a PERMANENT docked complementary panel beside a body (the
//     file viewer's side panel). No open/close — it's page furniture.
//
// THE ACTS (ADR-036 §7 — the two-control law on the panel): the panel composes
// its acts from the entity's ONE registry — `acts`, the resolved set — on BOTH
// hosts, so no surface hand-wires a button: the TOP row = the primary as a
// QUIET button (the peek stays calm — never the brand fill; a primary holding
// several outcomes opens them as a menu, "Decide ▾") + the Edit menu
// (`EditMenu`: one edit a plain button, several fold); no row when the viewer
// has neither. A PAGELESS entity (a zone, a cluster, an administrative area, a
// product, a contract configuration — the peek is its only surface) keeps its
// sectioned edits on the peek's own cards (`SectionAct`, as a page's cards
// would) — the Edit menu then holds only the section-less ones (ADR-036 §7,
// exception 1; a FILE, pageless too, keeps its edits in the Edit menu — the rider). The FOOTER = the labelled Actions door at the start (`doorOf` —
// operations · lifecycle · danger, fenced; never an edit, never the primary) +
// the trailing extras a host adds (`footer` — the file's walk pager) + "Open
// full page" where a full-page surface exists.
//
// THE IN-PLACE COMMIT (`commit`): an editor opened IN the body (the file's "What
// does it show?" — the picture must stay in view, so never a drawer) hands the
// panel its Cancel · Save; while it is open the footer IS that commit — in place
// of the door, the extras and "Open full page" — and the top row stands down, on
// both hosts, so its Save never scrolls out of reach however long the editor
// runs. It holds the panel's acts, not the panel: the host's own controls (a
// photo's expand, a version row) still act. When it closes and took the focus
// with it, the focus lands on the top row's first control.
//
// Props-only rung-2 composite (COMPONENTS §taxonomy): the URL binding lives in
// the page via `useEntityPeek`; the body (`children`) is the entity summary, a
// per-entity slice decision. A list's walk that steps the open peek from row to
// row is the list's (`useRowWalk` — FRONTEND-PATTERNS §Row interaction) — NOT here.

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_SLOT, Heading, HeadingContext } from 'react-aria-components';
import { icons } from '../ui/icons.js';
import { SlideOver } from '../ui/SlideOver.js';
import { HScroll } from '../ui/HScroll.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Link } from '../ui/Link.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { OVERLAY_SHEET } from '../../lib/keyOwner.js';
import { walkHome } from '../../stores/rowWalkStore.js';
import { useOverlayLeave, useOverlayTrail } from '../../hooks/useUrlOverlay.js';
import { Tooltip } from '../ui/Tooltip.js';
import { ActionMenu } from './ActionMenu.js';
import { EditMenu } from './EditMenu.js';
import { doorOf, editsOf, outcomeRows, primaryOf, runAct, type EntityAct } from './entityRegistry.js';

/** The min-width at/above which the peek is a non-modal side panel; below, the modal sheet. */
export const PEEK_WIDE_QUERY = '(min-width: 48rem)';

export interface ContextPanelProps {
  /** `peek` = the face inside the peek's sheet (`PeekSheet`); `rail` = permanent docked panel. */
  host: 'peek' | 'rail';
  /** Hero name (the entity / "Properties"). */
  title: string;
  /** Muted eyebrow above the title — the entity KIND (Site, Asset). Optional. */
  kind?: ReactNode;
  /** Optional leading identity visual (an `Avatar` for a person) shown before the kind/title. */
  leading?: ReactNode;
  /** Status pill beside the title — the caller composes the Badge (status is domain). */
  status?: ReactNode;
  /** The summary body — caller-composed (a slice decision). */
  children: ReactNode;
  /** The entity's resolved acts (`resolveActs` — the per-entity module's hook):
   *  the panel draws the top row (the primary · the Edit menu) and the footer
   *  door from them. The overlays the acts open are mounted by the caller. */
  acts?: readonly EntityAct[];
  /** The entity has no page — the peek is its only surface: its sectioned edits
   *  live on the peek's cards (`SectionAct` by section), not in the Edit menu
   *  (ADR-036 §7, exception 1). */
  pageless?: boolean;
  /** Trailing footer content beside "Open full page" (the file's walk pager · a
   *  record's updated stamp). */
  footer?: ReactNode;
  /** An in-place editor is open in the body: the footer becomes its Cancel · Save
   *  and the top row stands down until it closes (the header comment). */
  commit?: PanelCommit;
  /** peek only — the entity's canonical route. When set, ContextPanel renders the
   *  standard "Open full page" link: a REPLACE of the peek's own history entry (the
   *  first open PUSHED it — `useEntityPeek`; the commit consumes it), so Back from
   *  the page lands on the host — the list with its filters, the page the peek was
   *  opened from — with the peek closed, in one press (ADR-036 §2; FRONTEND-PATTERNS
   *  §Entity surfaces). No `onClose` on the commit: the route change unmounts the
   *  peek, and closing on a ⌘-click would disturb the tab being left behind. */
  openFullPath?: string;
  /** peek only — close (the header's X; Escape and the phone's backdrop are the sheet's). */
  onClose?: () => void;
  /** peek only — the sheet's width query: the face lays out as the side panel at/above it. Default md. */
  wideQuery?: string;
  /** peek only — the overlay held in the link this face shows (`useUrlOverlay`'s param): while a link was followed
   *  inside it, the back chevron takes the leading slot (its way back — the name of the record it returns to is the
   *  chevron's tooltip and accessible name). The entity peek by default; the file viewer and the import run name
   *  their own (nothing inside them follows). */
  overlay?: string;
  /** rail only — the peek's sheet takes its own. */
  className?: string;
}

/** The commit of an in-place editor open in the panel's body. */
export interface PanelCommit {
  onCancel: () => void;
  onSave: () => void;
  /** Save waits for an answer (nothing picked yet). */
  saveDisabled?: boolean;
}

export function ContextPanel({
  host,
  title,
  kind,
  leading,
  status,
  children,
  acts = [],
  pageless = false,
  footer,
  commit,
  openFullPath,
  onClose,
  wideQuery = PEEK_WIDE_QUERY,
  overlay = 'peek',
  className = '' }: ContextPanelProps) {
  const wide = useMediaQuery(wideQuery);
  const { t } = useTranslation('common');
  // The way back (a link followed inside the sheet): its last record is where the chevron returns. Held through the
  // sheet's exit — a close takes the overlay out of the address while the face slides away, and the chevron stays
  // as it was rather than swapping back to the glyph mid-slide.
  const { trail, back } = useOverlayTrail(overlay);
  const trailKey = JSON.stringify(trail);
  const [shown, setShown] = useState({ key: trailKey, trail });
  const closing = host === 'peek' && !new URLSearchParams(window.location.search).has(overlay);
  if (!closing && shown.key !== trailKey) setShown({ key: trailKey, trail });
  const returnTo = host === 'peek' ? shown.trail.at(-1) : undefined;
  const { onCommitClick } = useOverlayLeave();
  const navigate = useNavigate();

  // ── The acts, composed once for both hosts ──
  // The top row: the primary (a quiet button) + the Edit menu — nothing when the
  // viewer has neither (then no row is drawn).
  const primary = primaryOf(acts);
  const edits = editsOf(acts).filter((a) => !(pageless && a.section !== undefined));
  // The top row's controls, for the focus an editor's close hands back (a `contents` box — the row's layout is its children's).
  const topRowRef = useRef<HTMLSpanElement>(null);
  const committing = commit !== undefined;
  const wasCommitting = useRef(committing);
  useEffect(() => {
    if (wasCommitting.current && !committing) {
      const active = document.activeElement;
      if (!active || active === document.body) topRowRef.current?.querySelector<HTMLElement>('button:not([disabled]), a[href]')?.focus();
    }
    wasCommitting.current = committing;
  }, [committing]);
  const topRow = commit ? undefined : (
    primary || edits.length > 0 ? (
      <span ref={topRowRef} className="contents [&>*]:shrink-0">
        {primary &&
          (primary.outcomes && !primary.disabled ? (
            // One act, several outcomes (Decide ▾): the quiet main button opens them (a disabled one is a plain, disabled button).
            <ActionMenu label={primary.label} items={outcomeRows(primary)} trigger="labelled" placement="bottom start" />
          ) : (
            <Button variant="secondary" disabled={primary.disabled} onClick={() => primary.run && runAct(primary.run, navigate)}>
              {primary.label}
            </Button>
          ))}
        <EditMenu acts={edits} label={t('entity.edit')} />
      </span>
    ) : undefined
  );
  // The door: the operate rows, fenced — the labelled face on the panel (the
  // door never shrinks to a bare icon here: the footer has the room, and on
  // touch a labelled door is the discoverable one).
  const doorRows = doorOf(acts, 'peek');
  const door = doorRows.length > 0 ? <ActionMenu label={t('entity.actions')} items={doorRows} trigger="labelled" placement="top start" /> : undefined;
  const start = door;
  const end = footer;
  // The in-place commit — Cancel · Save at the end; on the phone's sheet the two share the row.
  const commitRow = commit ? (
    <div className={`flex w-full items-center gap-2 ${host === 'peek' && !wide ? '[&>*]:flex-1' : 'justify-end'}`}>
      <Button variant="secondary" onClick={commit.onCancel}>
        {t('actions.cancel')}
      </Button>
      <Button variant="primary" onClick={commit.onSave} disabled={commit.saveDisabled}>
        {t('actions.save')}
      </Button>
    </div>
  ) : undefined;

  // Peek footer = the door (start) + the extras and the standard Open-full-page
  // commit (end). The commit USES UP the peek's own history step (ADR-036 §7 —
  // FRONTEND-PATTERNS §Action & edit model, history hygiene: a surface that opened
  // a history entry must CONSUME it, never push its successor on top of it): the
  // link REPLACES the peek's entry with the entity route, so Back returns to the
  // host with the peek closed, exactly once. When the place under the peek changed
  // while it was open (a filter, a page — the desk's list stays live beside it),
  // the entry below still shows the place as it was; the click then keeps the
  // place as the person left it first and pushes the page after it
  // (`useOverlayLeave`), so Back returns to what they saw.
  //
  // No `onClose` here: the route change unmounts the peek by itself (the panel
  // renders from `?peek=`), and firing it would also wrongly close the peek on a
  // ⌘-click, which opens a new tab and must leave this one untouched.
  //
  // The row is ONE honest full-width flex (the door cannot be pushed off by the
  // link, and a consumer never needs its own justify wrapper).
  const peekFooter = commitRow ?? (
    start || end || openFullPath ? (
      <div className="flex w-full items-center justify-between gap-3">
        {/* flex-1 so a caller's own trailing context (a timestamp, a count) can
            justify inside the cluster; door-only callers stay start-aligned. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{start}</div>
        {(end || openFullPath) && (
          <div className="flex shrink-0 items-center gap-3">
            {end}
            {openFullPath && (
              <Link to={openFullPath} replace onClick={(e) => onCommitClick(e, openFullPath)} className="shrink-0 font-medium">
                {t('entity.openFullPage')}
              </Link>
            )}
          </div>
        )}
      </div>
    ) : undefined);

  // ── rail: a permanent docked complementary panel (the page provides the grid). ──
  // The same door-at-start + extras-at-end row; a docked panel has no "open full page".
  if (host === 'rail') {
    const railFooter =
      commitRow ??
      (start || end ? (
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{start}</div>
          {end && <div className="flex shrink-0 items-center gap-2">{end}</div>}
        </div>
      ) : undefined);
    return (
      <RailPanel leading={leading} kind={kind} title={title} status={status} topRow={topRow} footer={railFooter} className={className}>
        {children}
      </RailPanel>
    );
  }

  // ── peek: the FACE — the sheet around it is the host's (`PeekSheet`), so this
  //    renders the panel's rows straight into the sheet's column. The header is
  //    the SAME identity lockup at both widths (glyph · kind · title · status · X),
  //    its title the sheet's name. ──
  // The back chevron TAKES the leading slot while followed (the glyph, the avatar or the star step aside and return
  // with the first record) — no extra row; X still closes the whole peek (ADR-036 §2).
  const backLabel = returnTo ? t('entity.backTo', { name: returnTo.label }) : '';
  const header = (
    <PanelHeader
      leading={
        returnTo ? (
          // Below the chevron: the sheet's top edge leaves no room above it (the tip would flip onto the title).
          <Tooltip content={backLabel} placement="bottom">
            <IconButton variant="ghost" aria-label={backLabel} onClick={back}>
              <icons.caretLeft size={18} aria-hidden className="rtl:-scale-x-100" />
            </IconButton>
          </Tooltip>
        ) : (
          leading
        )
      }
      kind={kind}
      title={title}
      status={status}
      onClose={onClose}
      closeLabel={t('actions.close')}
    />
  );
  // Narrow: the top row rides the scroll body (on a phone the record gets the height).
  if (!wide) {
    return (
      <>
        {header}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {topRow && (
            <div className="mb-3 border-b border-[var(--color-border)] pb-3">
              {/* Actions stay on ONE row and scroll horizontally (self-hiding edge-fade)
                  rather than wrapping into a ragged stack — clean full labels, constant
                  vertical footprint, any language. */}
              <HScroll className="flex items-center gap-2 [&>*]:shrink-0" fadeColor="var(--color-bg-overlay)">
                {topRow}
              </HScroll>
            </div>
          )}
          {/* Same section stack rhythm as PanelBody. */}
          <div className="flex flex-col gap-3">{children}</div>
        </div>
        {peekFooter && <PanelFooter>{peekFooter}</PanelFooter>}
      </>
    );
  }
  return (
    <>
      {header}
      {topRow && <PanelTopRow fadeColor="var(--color-bg-overlay)">{topRow}</PanelTopRow>}
      <PanelBody>{children}</PanelBody>
      {peekFooter && <PanelFooter>{peekFooter}</PanelFooter>}
    </>
  );
}

// The shared header — kind eyebrow · title · status · (close, peek only). On the
// rail the `<h2 id>` names the aside (`aria-labelledby`); in a peek the title is
// the sheet's title slot — the sheet takes its name from it (React Aria's Dialog
// on a phone, the wide sheet's own slot id on a desk), and keeps it across face
// swaps (the sheet outlives the face). Either way the name is
// never a duplicated string.
function PanelHeader({
  leading,
  kind,
  title,
  status,
  onClose,
  closeLabel,
  headingId,
}: {
  leading?: ReactNode;
  kind?: ReactNode;
  title: string;
  status?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  /** The rail's `aria-labelledby` target; absent = a peek face (the sheet's title slot). */
  headingId?: string;
}) {
  // A plain <div>, NOT <header>: a <header> inside the dialog/aside is an
  // unscoped `banner` landmark that collides with the shell Navbar's banner
  // (the EntityHeader avoids <header> for the same reason). The dialog/aside is
  // named by the heading — the header element role is moot.
  const headingClass = 'min-w-0 truncate text-base font-semibold text-[var(--color-text-primary)]';
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        {/* The leading slot is a fixed optical box: a bare 22px glyph, an Avatar,
            or a BrandDot all CENTER against the kind/title stack — consumers pass
            the raw visual and never nudge alignment themselves (no mt-0.5). */}
        {leading && (
          <div className="flex min-h-9 min-w-9 shrink-0 items-center justify-center">{leading}</div>
        )}
        <div className="min-w-0">
          {kind && <div className="text-xs text-[var(--color-text-secondary)]">{kind}</div>}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {headingId ? (
              <h2 id={headingId} className={headingClass}>
                {title}
              </h2>
            ) : (
              <Heading slot="title" level={2} className={headingClass}>
                {title}
              </Heading>
            )}
            {status}
          </div>
        </div>
      </div>
      {onClose && (
        <IconButton aria-label={closeLabel ?? ''} onClick={onClose} variant="ghost">
          <icons.close aria-hidden size={18} />
        </IconButton>
      )}
    </div>
  );
}

// The top row of acts — the primary + the Edit menu on ONE scrolling row
// (self-hiding edge-fade) rather than a ragged wrap: clean full labels, a
// constant vertical footprint, any language. Same band on both hosts.
function PanelTopRow({ children, fadeColor }: { children: ReactNode; fadeColor: string }) {
  return (
    <div className="border-b border-[var(--color-border)] px-5 py-3">
      <HScroll className="flex items-center gap-2 [&>*]:shrink-0" fadeColor={fadeColor}>
        {children}
      </HScroll>
    </div>
  );
}

// The body owns the section STACK RHYTHM (gap-3) — peeks stop wrapping their
// sections in per-peek stack divs with divergent gaps.
function PanelBody({ children }: { children: ReactNode }) {
  return <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">{children}</div>;
}

// A plain <div>, NOT <footer>: an unscoped <footer> is a `contentinfo` landmark
// (same landmark-collision reasoning as PanelHeader above). No own background —
// it inherits the host root (overlay-white on the peek, surface on the rail), so
// the band never mismatches its panel in dark mode.
function PanelFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
      {children}
    </div>
  );
}

// The permanent rail — a complementary landmark; fills its grid cell (the page
// owns placement + any stickiness). border-s (logical) sits it against the body.
function RailPanel({
  leading,
  kind,
  title,
  status,
  topRow,
  footer,
  children,
  className,
}: {
  leading?: ReactNode;
  kind?: ReactNode;
  title: string;
  status?: ReactNode;
  topRow?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className: string;
}) {
  const headingId = useId();
  return (
    <aside
      aria-labelledby={headingId}
      className={`flex h-full flex-col border-s border-[var(--color-border)] bg-[var(--color-bg-surface)] ${className}`}
    >
      <PanelHeader leading={leading} kind={kind} title={title} status={status} headingId={headingId} />
      {topRow && <PanelTopRow fadeColor="var(--color-bg-surface)">{topRow}</PanelTopRow>}
      <PanelBody>{children}</PanelBody>
      {footer && <PanelFooter>{footer}</PanelFooter>}
    </aside>
  );
}


export interface PeekSheetProps {
  /** Whether the peek is open — the host's `?peek=`, or the self-hosted peek's own state. */
  open: boolean;
  /** Close — Escape, and the backdrop on a phone (the face's X calls the face's own `onClose`). */
  onClose: () => void;
  /** The face: a `ContextPanel host="peek"`, with whatever its peek mounts beside it. */
  children: ReactNode;
  /** The min-width at/above which the sheet is the non-modal side panel. Default md. */
  wideQuery?: string;
  /** The wide panel's extra classes. */
  className?: string;
  /** The URL param of the overlay the sheet holds (`peek` · `file` · `job`) — its face's mark (lib/keyOwner): a sheet
   *  is never a modal over its OWN overlay's keys, always over another's. Default the peek. */
  overlay?: string;
}

// The peek's SHEET — the overlay, ONE for as long as the peek is open. Its host
// mounts it around whatever the open ref renders, so nothing the face does — a
// loader first, a step to another row, a link to another kind of record —
// replays the entrance: the sheet slides in when the peek opens, never again
// while it stays open (DESIGN-TOKENS §Motion). Modality follows width (the header comment);
// the phone's form is the SlideOver carrying the face whole, capped at the wide
// panel's 24rem.
//
// The sheet outlives `open` by its exit: closed (X · Escape · Back — and on a
// phone a tap on the scrim), it holds the face it showed and slides out — the drawers' exit, which
// the global reduced-motion block collapses to nothing. At the exit's START it
// leaves the accessibility tree and the tab order (`inert`) and the focus goes
// back to where the person opened it from — or, when a list's walk stepped the
// peek on from that row, to the same control in the row the walk stopped on
// (stores/rowWalkStore) — only while the focus is still the sheet's — never at its end. A page changing under it (Open full page, a tab,
// any route) CUTS it, and so does a change of form (the window crossing the wide
// query): page changes are not animated. A re-open during the exit takes the
// same sheet back — never two.
export function PeekSheet({ open, onClose, children, wideQuery = PEEK_WIDE_QUERY, className = '', overlay = 'peek' }: PeekSheetProps) {
  const wide = useMediaQuery(wideQuery);
  const { pathname } = useLocation();
  // The page as the address shows it: a navigation writes the address before either the router's or the query's
  // state renders, and the query's (the overlay closing) can render first — the page change must win that race.
  const address = typeof window === 'undefined' ? pathname : window.location.pathname;
  const [held, setHeld] = useState<HeldSheet | null>(null);
  // Adjusted while rendering (React's "information from previous renders"): the face is held while open, the exit
  // starts when `open` drops on the same page in the same form, and a page or form change under a closing sheet ends
  // it at once (a phone close leaves React Aria's drawer to unmount itself — a later widen must never replay it).
  const moved = (h: HeldSheet) => h.path !== pathname || h.address !== address || h.wide !== wide;
  if (open) {
    if (!held || held.exiting || held.face !== children || moved(held))
      setHeld({ face: children, path: pathname, address, wide, exiting: false, opener: held && !held.exiting ? held.opener : focusedElement() });
  } else if (held && (!held.exiting || moved(held))) {
    setHeld(held.exiting || moved(held) ? null : { ...held, exiting: true });
  }

  const boxRef = useRef<HTMLDivElement>(null);
  // Where the focus goes back to: the opener, refreshed by a fresh pick from the page beside the wide sheet (a row's
  // link, a cell) — the latest place outside the sheet the person put the focus. Reset at each open.
  const returnTo = useRef<HTMLElement | null>(null);
  // The phone's hand-back, waiting for React Aria to lift the page's own inert (a passive cleanup of its overlay).
  const handBack = useRef<HTMLElement | null>(null);
  const exiting = held?.exiting ?? false;
  const opener = held?.opener ?? null;
  useEffect(() => {
    if (!open) return;
    returnTo.current = opener;
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement) || boxRef.current?.closest('[role="dialog"]')?.contains(target)) return;
      if (target.closest('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      returnTo.current = target;
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open, opener]);

  // The exit's start: out of the tree and the tab order, the focus handed back, then gone once the slide has run.
  useLayoutEffect(() => {
    if (!exiting) return;
    const sheet = boxRef.current?.closest<HTMLElement>('[role="dialog"]');
    if (!sheet) return;
    const active = document.activeElement;
    const ours = active === null || active === document.body || sheet.contains(active);
    const opener = returnTo.current?.isConnected ? returnTo.current : null;
    const target = ours ? ((overlay === 'peek' ? walkHome(opener) : null) ?? opener) : null;
    sheet.inert = true;
    // The wide sheet hands the focus back now; the phone's waits for the passive phase, where React Aria's overlay
    // lifts the page's inert first (React runs every passive cleanup before any passive setup).
    if (wide) target?.focus({ preventScroll: true });
    else handBack.current = target;
    // The wide sheet ends on its own slide; the phone's drawer is React Aria's, which unmounts itself.
    // The slide's end removes the sheet in the SAME moment, before the browser paints again — React Aria's own exit
    // contract (the drawers, the modals): a sheet left for a frame past its animation shows at rest, a flash.
    let cancelled = false;
    if (wide) {
      const slides = typeof sheet.getAnimations === 'function' ? sheet.getAnimations() : [];
      void Promise.allSettled(slides.map((a) => a.finished)).then(() => {
        if (!cancelled) flushSync(() => setHeld((h) => (h?.exiting ? null : h)));
      });
    }
    // A re-open during the exit takes the same sheet back — in the tree again, its slide's end no longer ending it.
    return () => {
      cancelled = true;
      sheet.inert = false;
    };
  }, [exiting, wide, overlay]);
  useEffect(() => {
    const target = handBack.current;
    handBack.current = null;
    if (exiting && target?.isConnected) target.focus({ preventScroll: true });
  }, [exiting, wide]);

  if (!held) return null;
  const face = (
    <KeepFocus boxRef={boxRef} overlay={overlay}>
      {open ? children : held.face}
    </KeepFocus>
  );
  if (!wide) return <SlideOver open={open} onClose={onClose} maxWidth="max-w-sm" face={face} />;
  return (
    <WideSheet exiting={!open} onClose={onClose} className={className}>
      {face}
    </WideSheet>
  );
}

interface HeldSheet {
  /** The face shown — held through the exit. */
  face: ReactNode;
  /** The page the sheet opened on — the router's and the address's: a close on another page is a page change, cut. */
  path: string;
  address: string;
  /** The form it shows (the wide query's answer): a closing sheet whose form changed is cut. */
  wide: boolean;
  exiting: boolean;
  /** The element focused when the sheet opened. */
  opener: HTMLElement | null;
}

function focusedElement(): HTMLElement | null {
  const active = typeof document === 'undefined' ? null : document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

// The face swaps under the focus (the record arriving, a link to another kind of
// record): when the control holding it goes with the old face, the focus would
// fall to the page — the sheet takes it back, so a keyboard or screen-reader
// user keeps their place. A focus the person moved out themselves is let go.
// And the sheet's NAME moves with the face: the sheet took the focus on open,
// while the face still showed its loading title, so when the name changes under
// a focus resting on the sheet itself, the sheet re-takes it and a screen reader
// announces the record (the move React Aria's Dialog makes for VoiceOver).
// A `contents` box: the face's rows stay the sheet's own flex children. It marks the face as an overlay's own sheet
// (`OVERLAY_SHEET`, naming its overlay): the phone's drawer around it frames that overlay, never a modal over its own
// keys — a sheet of ANOTHER overlay over it (the photo viewer over the peek) still is one (lib/keyOwner).
function KeepFocus({ children, boxRef, overlay }: { children: ReactNode; boxRef: RefObject<HTMLDivElement | null>; overlay: string }) {
  useEffect(() => {
    const box = boxRef.current;
    const sheet = box?.closest<HTMLElement>('[role="dialog"]');
    if (!box || !sheet) return;
    const nameOf = () => document.getElementById(sheet.getAttribute('aria-labelledby') ?? '')?.textContent ?? '';
    let name = nameOf();
    let held: Element | null = null;
    const onIn = (e: FocusEvent) => {
      held = e.target as Element;
    };
    const onOut = (e: FocusEvent) => {
      if (e.relatedTarget instanceof Node && box.contains(e.relatedTarget)) return;
      const from = e.target as Element;
      setTimeout(() => {
        if (held === from && from.isConnected) held = null;
      }, 0);
    };
    const observer = new MutationObserver(() => {
      const active = document.activeElement;
      const renamed = nameOf() !== name;
      name = nameOf();
      if (sheet.inert) return;
      if (held && !held.isConnected && (active === null || active === document.body)) {
        held = null;
        sheet.focus();
      } else if (renamed && active === sheet) {
        sheet.blur();
        sheet.focus();
      }
    });
    box.addEventListener('focusin', onIn);
    box.addEventListener('focusout', onOut);
    observer.observe(box, { childList: true, subtree: true, characterData: true });
    return () => {
      box.removeEventListener('focusin', onIn);
      box.removeEventListener('focusout', onOut);
      observer.disconnect();
    };
  }, [boxRef]);
  return (
    <div ref={boxRef} className="contents" {...{ [OVERLAY_SHEET]: overlay }}>
      {children}
    </div>
  );
}

// The hand-rolled NON-MODAL wide sheet. Named by the face's title through React
// Aria's title slot — the same wiring as the phone's Dialog, so both widths take
// their name one way. NOT React Aria's Dialog: this one is non-modal — the list
// and its keys stay live beside it (lib/keyOwner). Docks at the inline-end edge below
// the frame's pinned top (`--shell-top` — the project strips + the nav, measured
// by lib/shellTop; never a literal); content behind stays interactive (no scrim).
// z-overlay floats it above content; menus DO open from inside it (the footer
// Actions door, per-row ⋯ menus) — React Aria popovers carry an inline
// z-index:100000 from the foundation, so they always paint above this panel (the
// z-scale note in index.css records the rule). APG non-modal-dialog behaviour:
// focus moves IN on open, Escape closes, focus returns on close (PeekSheet —
// at the exit's start, only while the focus is still the sheet's: a person who
// moved on into the list beside it keeps their place); focus is NOT trapped (the
// list stays reachable — a click through to it works, sort included). Exiting,
// it slides out with the drawers' exit and Escape no longer reaches it.
function WideSheet({ exiting, onClose, className, children }: { exiting: boolean; onClose: () => void; className: string; children: ReactNode }) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  // The entrance runs once and is then taken off, so the exit — the same keyframes run in reverse — is a NEW
  // animation the browser plays (swapping a finished animation's direction plays nothing). A close inside the
  // entrance bends the running slide instead (every React Aria drawer does the same); a re-open during the exit
  // takes the sheet back at rest, never replaying the entrance — and takes the focus in, as an open does.
  const [entering, setEntering] = useState(true);
  if (exiting && entering) setEntering(false);
  const wasExiting = useRef(exiting);
  useLayoutEffect(() => {
    if (wasExiting.current && !exiting) sheetRef.current?.focus({ preventScroll: true });
    wasExiting.current = exiting;
  }, [exiting]);
  // onClose via a ref so the mount-only effect always calls the current handler
  // without re-running.
  const onCloseRef = useRef(onClose);
  const exitingRef = useRef(exiting);
  useEffect(() => {
    onCloseRef.current = onClose;
    exitingRef.current = exiting;
  });

  // Focus in, and Escape (document-level, so it closes after the focus moved to the
  // live list; a nested overlay — a Menu — that handled it first wins).
  useLayoutEffect(() => {
    // SR reads "dialog, <title>" via aria-labelledby; docked, it never scrolls the page under it.
    sheetRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !exitingRef.current) {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={sheetRef}
      role="dialog"
      aria-labelledby={headingId}
      tabIndex={-1}
      data-exiting={exiting || undefined}
      className={
        'fixed bottom-0 end-0 top-[var(--shell-top)] z-overlay flex w-[min(24rem,100vw)] flex-col ' +
        'border-s border-[var(--color-border)] bg-[var(--color-bg-overlay)] text-[var(--color-text-primary)] ' +
        `shadow-modal outline-none ${exiting ? 'o-drawer-out' : entering ? 'o-drawer-in' : ''} [--drawer-from:100%] rtl:[--drawer-from:-100%] ` +
        className
      }
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && !exiting) setEntering(false);
      }}
    >
      <HeadingContext.Provider value={{ slots: { [DEFAULT_SLOT]: {}, title: { id: headingId } } }}>{children}</HeadingContext.Provider>
    </div>
  );
}
